import React, { useEffect, useRef } from 'react';
import { useWatch } from 'react-hook-form';
import type { AuditDynamicFormSyncProps } from '../../types/auditFormTypes';

const AuditDynamicFormSync: React.FC<AuditDynamicFormSyncProps> = React.memo(({
  control,
  getValues,
  onFormDataUpdate,
  delayMs = 120,
}) => {
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previousTicketCountRef = useRef<number | null>(null);
  const watchedAction = useWatch({
    control,
    name: 'action',
  });
  const watchedTicketDetails = useWatch({
    control,
    name: 'ticket_details',
  });
  const watchedTicketCount = Array.isArray(watchedTicketDetails) ? watchedTicketDetails.length : 0;

  useEffect(() => {
    if (!onFormDataUpdate) {
      return;
    }

    const previousTicketCount = previousTicketCountRef.current;
    previousTicketCountRef.current = watchedTicketCount;

    if (syncTimeoutRef.current) {
      clearTimeout(syncTimeoutRef.current);
      syncTimeoutRef.current = null;
    }

    if (previousTicketCount !== null && previousTicketCount !== watchedTicketCount) {
      const currentFormData = getValues();
      onFormDataUpdate({
        ...currentFormData,
        action: watchedAction,
        ticket_details: watchedTicketDetails,
      });

      return;
    }

    syncTimeoutRef.current = setTimeout(() => {
      const currentFormData = getValues();
      onFormDataUpdate({
        ...currentFormData,
        action: watchedAction,
        ticket_details: watchedTicketDetails,
      });
    }, delayMs);

    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
    };
  }, [delayMs, getValues, onFormDataUpdate, watchedAction, watchedTicketCount, watchedTicketDetails]);

  return null;
});

AuditDynamicFormSync.displayName = 'AuditDynamicFormSync';

export default AuditDynamicFormSync;
